import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import useDeleteData from "../../../../hooks/useDeleteData";
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import Create from "./Create";
const List = () => {
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await Http.get(`/project`);
      setProjects(res.data.data);
    } catch (error) {
      console.error("Fetch error:", error);
      setAlert({
        type: "error",
        message: "Failed to load projects. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to add a single project to the list without fetching all projects
  const addProjectToList = (project) => {
    setProjects((prevProjects) => [project, ...prevProjects]);
    setAlert({
      type: "success",
      message: "Project created successfully!",
    });

    // Clear alert after 3 seconds
    setTimeout(() => {
      setAlert(null);
    }, 3000);
  };

  useEffect(() => {
    DatatableFunc();
    fetchData();
  }, []);

  const deleteData = useDeleteData((id) => {
    // Update state locally instead of re-fetching all data
    setProjects((prevProjects) => prevProjects.filter((project) => project.id !== id));
    setAlert({
      type: "success",
      message: "Project deleted successfully!",
    });

    // Clear alert after 3 seconds
    setTimeout(() => {
      setAlert(null);
    }, 3000);
  });

  // Filter projects based on search term and status
  const filteredProjects = projects.filter((project) => {
    const matchesSearch =
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = filterStatus === "all" || project.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  // Get status tag color
  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "completed":
        return "info";
      case "on-hold":
        return "warning";
      case "cancelled":
        return "danger";
      default:
        return "secondary";
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Truncate text for display
  const truncateText = (text, maxLength = 100) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  // Strip HTML tags from text
  const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const handleAddProject = () => {
    setVisible(true);
  };

  const handleCloseDialog = () => {
    setVisible(false);
  };

  return (
    <div>
      <IsLogin />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Project Management</h5>
          <div className="d-flex justify-content-between align-items-center w-100 mt-2">
            <div className="d-flex align-items-center">
              <span className="p-input-icon-left mr-3" style={{ width: "300px" }}>
                <i className="pi pi-search" style={{ marginLeft: "4px" }} />
                <InputText
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search projects..."
                  className="w-100"
                  style={{ paddingLeft: "20px" }}
                />
              </span>
              <div className="ml-3">
                <Button
                  label="All"
                  className={`p-button-sm ${filterStatus === "all" ? "p-button-info" : "p-button-outlined p-button-info"}`}
                  onClick={() => setFilterStatus("all")}
                  style={{ marginRight: "5px" }}
                />
                <Button
                  label="Active"
                  className={`p-button-sm ${filterStatus === "active" ? "p-button-success" : "p-button-outlined p-button-success"}`}
                  onClick={() => setFilterStatus("active")}
                  style={{ marginRight: "5px" }}
                />
                <Button
                  label="Completed"
                  className={`p-button-sm ${filterStatus === "completed" ? "p-button-info" : "p-button-outlined p-button-info"}`}
                  onClick={() => setFilterStatus("completed")}
                  style={{ marginRight: "5px" }}
                />
                <Button
                  label="On Hold"
                  className={`p-button-sm ${filterStatus === "on-hold" ? "p-button-warning" : "p-button-outlined p-button-warning"}`}
                  onClick={() => setFilterStatus("on-hold")}
                  style={{ marginRight: "5px" }}
                />
                <Button
                  label="Cancelled"
                  className={`p-button-sm ${filterStatus === "cancelled" ? "p-button-danger" : "p-button-outlined p-button-danger"}`}
                  onClick={() => setFilterStatus("cancelled")}
                />
              </div>
            </div>
            <Button label="Create Project" icon="pi pi-plus" className="p-button-success" onClick={handleAddProject} />
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          {loading ? (
            <div className="text-center p-5">
              <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
              <p className="mt-2">Loading projects...</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center p-5">
              <i className="pi pi-folder-open" style={{ fontSize: "3rem", color: "#ccc" }}></i>
              <p className="mt-2">No projects found. Create your first project!</p>
            </div>
          ) : (
            <div className="row">
              {filteredProjects.map((project) => (
                <div className="col-md-4 mb-4" key={project.id}>
                  <div className="card h-100 shadow-sm">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h5 className="mb-0 text-truncate" title={project.title}>
                        {project.title}
                      </h5>
                      <Tag value={project.status} severity={getStatusColor(project.status)} />
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <p className="card-text">{truncateText(stripHtml(project.description), 150)}</p>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <small className="text-muted">
                          <i className="pi pi-calendar mr-1"></i> Start: {formatDate(project.start_date)}
                        </small>
                        <small className="text-muted">
                          <i className="pi pi-calendar-times mr-1"></i> End: {formatDate(project.end_date)}
                        </small>
                      </div>
                      <div className="mb-2">
                        {project.reference_links && project.reference_links.length > 0 && (
                          <small className="d-block text-muted mb-1">
                            <i className="pi pi-link mr-1"></i> {project.reference_links.length} Reference Links
                          </small>
                        )}
                        {project.server_credentials && project.server_credentials.length > 0 && (
                          <small className="d-block text-muted mb-1">
                            <i className="pi pi-server mr-1"></i> {project.server_credentials.length} Server Credentials
                          </small>
                        )}
                        {project.documents && project.documents.length > 0 && (
                          <small className="d-block text-muted">
                            <i className="pi pi-file mr-1"></i> {project.documents.length} Documents
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="card-footer bg-transparent d-flex justify-content-between">
                      {/* View Button */}
                      <Link to={`/view-project/${project.id}`} className="btn btn-sm btn-info">
                        <i className="pi pi-eye mr-1"></i> View
                      </Link>
                      <div>
                        {/* Edit Button */}
                        <Link to={`/edit-project/${project.id}`} className="btn btn-sm btn-warning mr-1">
                          <i className="pi pi-pencil"></i>
                        </Link>
                        {/* Delete Button */}
                        <button className="btn btn-sm btn-danger" onClick={() => deleteData(`/project/destroy/${project.id}`)}>
                          <i className="pi pi-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog header="Create Project" visible={visible} style={{ width: "70vw", zIndex: 1000 }} onHide={handleCloseDialog} dismissableMask>
        <Create setVisible={setVisible} fetchData={fetchData} />
      </Dialog>
    </div>
  );
};

export default List;
